import { AssistantChatStatus } from '@rematter/frontend-lib-api-client';
import { differenceInDays } from 'date-fns';
import { atom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';

import { assistantChatCreateMutationAtom } from '../_graphql/mutationAssistantChatCreate';
import { aiChatsQueryAtom } from '../_graphql/queryAssistantChats';

import { $selectedChatId } from './chatConversation.state';

// 🔘🔘🔘 PLAIN ATOMS - UI state
export const $searchTerm = atom<string>('');
export const $statusFilter = atomWithStorage<AssistantChatStatus | undefined>(
  'foreman-status-filter',
  undefined,
);

// Optimistic chat title updates (chatId -> title)
export const $optimisticChatTitles = atom<Map<string, string>>(new Map());

// 🟢🟢🟢 REMOTE DATA - Instantiate query atoms
export const $chats = aiChatsQueryAtom((get) => ({
  variables: {
    status: get($statusFilter) ?? 'Active',
    searchTerm: get($searchTerm) || undefined,
    pagination: { limit: 100, offset: 0 },
  },
}));

// Computed atom that merges server data with optimistic title updates
export const $chatsWithOptimisticTitles = atom((get) => {
  const queryResult = get($chats);
  const optimisticTitles = get($optimisticChatTitles);

  if (!queryResult.data?.chats || optimisticTitles.size === 0) {
    return queryResult;
  }

  // Merge optimistic titles with server data
  const chatsWithTitles = queryResult.data.chats.map((chat) => {
    const optimisticTitle = optimisticTitles.get(chat.id);
    return optimisticTitle ? { ...chat, title: optimisticTitle } : chat;
  });

  return {
    ...queryResult,
    data: {
      ...queryResult.data,
      chats: chatsWithTitles,
    },
  };
});

// 🟡🟡🟡 HIDDEN ACTIONS - Mutations
const $createChatMutation = assistantChatCreateMutationAtom({
  onSuccess: (get, set, { result }) => {
    get($chats).refetch();
    set($selectedChatId, result.assistantCreateChat.id);
  },
});

// 🟠🟠🟠 ACTIONS - Public actions
export const $createNewChat = atom(null, async (get, set) => {
  // System prompt is now automatically selected by the backend
  await set($createChatMutation, {});
});

export const $updateOptimisticChatTitle = atom(
  null,
  (get, set, { chatId, title }: { chatId: string; title: string }) => {
    const current = get($optimisticChatTitles);
    const updated = new Map(current);
    updated.set(chatId, title);
    set($optimisticChatTitles, updated);
  },
);

export const $autoSelectOrCreateChat = atom(null, async (get, set) => {
  const selectedChatId = get($selectedChatId);
  if (selectedChatId) return;

  const queryResult = get($chats);
  const chats = queryResult.data?.chats;
  if (!chats) return;

  if (chats.length === 0) {
    await set($createNewChat);
    return;
  }

  const mostRecentChat = chats[0];
  if (differenceInDays(new Date(), mostRecentChat.createdAt.toDate()) >= 1) {
    await set($createNewChat);
  } else {
    const mostRecentlyUpdated = [...chats].sort(
      (a, b) => b.updatedAt.toDate().getTime() - a.updatedAt.toDate().getTime(),
    )[0];
    set($selectedChatId, mostRecentlyUpdated.id);
  }
});
